import { Button } from "../ui/button";
import { Plus, Info } from "lucide-react";
import { useHobbiesManager, type HobbyItem } from "./useHobbiesManager";
import { HobbiesList } from "./HobbiesList";
import { HobbyEditForm } from "./HobbyEditForm";

interface AdminHobbiesTabProps {
  hobbies: HobbyItem[];
  onRefresh: () => void;
}

export function AdminHobbiesTab({ hobbies, onRefresh }: AdminHobbiesTabProps) {
  const sortedHobbies = [...hobbies].sort((a, b) => a.sort_order - b.sort_order);
  const {
    editingHobby,
    setEditingHobby,
    activeDraft,
    setActiveDraft,
    startAdd,
    handleEdit,
    handleSaveDraft,
    handleDiscardDraft,
    handleSave,
    handleDelete,
    handleMove
  } = useHobbiesManager(hobbies, onRefresh);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center pb-2 border-b border-border/80 text-left">
        <div>
          <h4 className="text-xl font-bold text-foreground uppercase tracking-wider">
            Curated Hobbies
          </h4>
          <p className="text-xs text-muted-foreground mt-0.5">Manage books, music albums, and films</p>
        </div>
        {!editingHobby && (
          <Button onClick={startAdd} className="bg-brand-primary text-white">
            <Plus className="h-4 w-4 mr-2" /> Add Item
          </Button>
        )}
      </div>

      <div className="flex items-start gap-2.5 p-3 rounded-xl bg-brand-primary/5 dark:bg-brand-primary/10 border border-brand-primary/10 text-brand-primary text-xs max-w-3xl leading-relaxed text-left">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <strong>Slider & Limits Configuration:</strong> You can add any number of items. If there
          are more than 2 items in a category, the public website displays them in a horizontal
          scroll-slider. A maximum of <strong>5 items</strong> per category will be displayed.
        </div>
      </div>

      {editingHobby ? (
        <HobbyEditForm
          editingHobby={editingHobby}
          setEditingHobby={setEditingHobby}
          onSave={handleSave}
          onCancel={() => {
            setEditingHobby(null);
            setActiveDraft(null);
          }}
          activeDraft={activeDraft}
          onSaveDraft={handleSaveDraft}
          onDiscardDraft={handleDiscardDraft}
          liveHobby={hobbies.find((h) => h.id === editingHobby.id)}
        />
      ) : (
        <HobbiesList
          hobbies={sortedHobbies}
          onMove={handleMove}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

export default AdminHobbiesTab;
